import { guardModalDismissReason } from "enumAddons/modalDismissReason";
import { CASHIER, REFILL } from "enums/cashier";
import {
  CASHIER_SCOPE,
  GLOBAL,
  REFILL_SCOPE,
  RIGHT_SIDE_REFILL_SCOPE,
  RIGHT_SIDE_SCOPE,
  SCENE,
  STREAM_SCOPE,
} from "enums/modalScope";
import { LoginPromotionType, ModalType } from "src/enums";
import { getEnabledPaymentProviders } from "src/environment";
import {
  openAppleAccountRestorationBottomScreen,
  openBottomSheetBottomScreen,
  openConfirmationBottomScreen,
  openDeleteAccountBottomScreen,
  openDeleteChatBottomScreen,
  openDeleteEmailBottomScreen,
  openDeleteMessageBottomScreen,
  openDeletePhoneNumberBottomScreen,
  openEditEmailBottomScreen,
  openEditPhoneNumberBottomScreen,
  openEnterReferralCodeBottomScreen,
  openJoinAgencyInfoBottomScreen,
  openLandingPageBottomScreen,
  openLandingV3BottomScreen,
  openPushNotificationPermissionRequestBottomSheet,
  openRefillDrawerBottomScreen,
  openRefillV2DrawerBottomScreen,
} from "state/actionCreators/bottomScreen";
/* eslint-disable no-restricted-imports */
import { DISMISS_MODAL, LOCK_MODAL, SHOW_MODAL } from "state/actionTypes";
/* eslint-enable no-restricted-imports */

export const lockTopModal = () => ({ type: LOCK_MODAL, payload: true });
export const unlockTopModal = () => ({ type: LOCK_MODAL, payload: false });
export const dismissModalWithType = ({ modalType, modalDismissReason }) =>
  guardModalDismissReason(modalDismissReason) && {
    type: DISMISS_MODAL,
    payload: { modalType, modalDismissReason },
  };
export const dismissTopModal = (modalDismissReason) =>
  guardModalDismissReason(modalDismissReason) && {
    type: DISMISS_MODAL,
    payload: { modalDismissReason },
  };

export const dismissBatchModals = (modalTypes) => ({
  type: DISMISS_MODAL,
  payload: { modalType: modalTypes },
  meta: { batchDismiss: true },
});

export const openModal = ({
  modalType,
  modalData,
  modalScope = SCENE,
  pathname = document.location.pathname,
  stayOnRedirect = false,
}) => ({
  type: SHOW_MODAL,
  payload: {
    modalType,
    modalData,
    modalScope,
  },
  meta: { pathname, stayOnRedirect },
});

export const openBuyCoinsModal = ({
  pricePointId,
  offer,
  offerConfig,
  provider,
  lockToProvider,
  purchaseSource,
  target,
  streamerId = "",
  uiComponent,
  previousUiComponent,
  viewType = CASHIER,
  isBackAvailable,
  promotionType,
  promotionValue,
  onSuccess,
  onError,
  onDismiss,
  withBonus = false,
  getWheelConfig,
  shouldDismissOnBack,
  stayOnRedirect,
  purchaseOrigin,
  analyticsParams,
  modalType = ModalType.BUY_COINS,
} = {}) => {
  const isRefill = viewType === REFILL;
  let modalScope = isRefill ? REFILL_SCOPE : CASHIER_SCOPE;

  if (
    modalType === ModalType.CASHIER_MODAL ||
    (modalType === ModalType.CHECKOUT && !isRefill)
  ) {
    modalScope = RIGHT_SIDE_SCOPE;
  } else if (
    modalType === ModalType.REFILL_MODAL ||
    (modalType === ModalType.CHECKOUT && isRefill)
  ) {
    modalScope = RIGHT_SIDE_REFILL_SCOPE;
  }

  return openModal({
    modalType,
    modalScope,
    modalData: {
      pricePointId,
      offer,
      offerConfig,
      provider,
      purchaseSource,
      target,
      lockToProvider,
      ...(streamerId && { streamerId }),
      uiComponent,
      previousUiComponent,
      viewType,
      isBackAvailable,
      promotionType,
      promotionValue,
      onSuccess,
      onError,
      onDismiss,
      withBonus,
      getWheelConfig,
      shouldDismissOnBack,
      purchaseOrigin,
      analyticsParams,
    },
    stayOnRedirect,
  });
};

export const openCheckoutModal = (props = {}) =>
  openBuyCoinsModal({
    ...props,
    modalType: ModalType.CHECKOUT,
  });

export const openPurchaseStatusModal = ({ operationId }) =>
  openModal({
    modalType: ModalType.PURCHASE_STATUS,
    modalScope: RIGHT_SIDE_SCOPE,
    modalData: {
      operationId,
    },
  });

export const openCashierModal = (props = {}) =>
  openBuyCoinsModal({
    ...props,
    modalType: ModalType.CASHIER_MODAL,
  });

export const openRefillModal = (props = {}) =>
  openBuyCoinsModal({
    ...props,
    modalType: ModalType.REFILL_MODAL,
  });

export const openLandingPageModal = ({ place, trackingId, onDismiss } = {}) =>
  openModal({
    modalType: ModalType.LANDING,
    modalData: {
      place,
      trackingId,
      onDismiss,
    },
  });

export const openLandingV3Modal = ({ place, trackingId, onDismiss } = {}) =>
  openModal({
    modalType: ModalType.LANDING_MODAL,
    modalScope: RIGHT_SIDE_SCOPE,
    modalData: {
      place,
      trackingId,
      onDismiss,
    },
  });

export const openDeleteAccountModal = () =>
  openModal({
    modalType: ModalType.DELETE_ACCOUNT_MODAL,
    modalData: {},
  });

export const openDeletePhoneNumberModal = () =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_DELETE_PHONE_NUMBER,
    modalData: {},
  });

export const openDeleteMessageRequestModal = ({ title }) =>
  openModal({
    modalType: ModalType.DELETE_MESSAGE_REQUEST,
    modalData: { title },
  });

export const openEditPhoneNumberModal = ({
  onOutsideClickDismiss,
  isSecureYourAccount,
}) =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_EDIT_PHONE_NUMBER,
    modalData: {
      onOutsideClickDismiss,
      isSecureYourAccount,
    },
  });

export const openDeleteEmailModal = ({ email }) =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_DELETE_EMAIL,
    modalData: { email },
  });

export const openDeleteChatMessageModal = ({ messages }) =>
  openModal({
    modalType: ModalType.CHAT_MESSAGE_DELETE_MODAL,
    modalData: { messages },
  });

export const openDeleteChatModal = (conversationId) =>
  openModal({
    modalType: ModalType.DELETE_CHAT_MODAL,
    modalData: { conversationId },
  });

export const openEditEmailModal = ({ email }) =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_EMAIL,
    modalData: { email },
  });

export const openEnterReferralCodeModal = () =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_ENTER_REFERRAL_CODE,
  });

export const openJoinAgencyInfoModal = () =>
  openModal({
    modalType: ModalType.PROFILE_SETTINGS_AGENCY_INFO,
  });

export const openCannotSendGiftModal = ({ target, modalScope } = {}) =>
  openModal({
    modalType: ModalType.CANNOT_SEND_GIFT,
    modalData: { target },
    modalScope,
  });

export const openNsfwContentModal = ({
  redirectLink,
  shouldRedirectOnClose = false,
  onOutsideClickDismiss = () => {},
  shouldUseCallback = false,
  callback = () => {},
}) =>
  openModal({
    modalType: ModalType.OPEN_NSFW_CONTENT_MODAL,
    modalData: {
      redirectLink,
      shouldRedirectOnClose,
      onOutsideClickDismiss,
      shouldUseCallback,
      callback,
    },
  });

export const openLoginModal = ({
  promotionType = LoginPromotionType.DEFAULT,
  registrationSource,
  accountId,
  redirectOnDismiss,
  onDismiss,
  onOutsideClickDismiss,
  title,
  onLoginSuccess,
  isBackButtonHidden,
} = {}) =>
  openModal({
    modalType: ModalType.LOGIN,
    modalData: {
      promotionType,
      registrationSource,
      accountId,
      redirectOnDismiss,
      onDismiss,
      onOutsideClickDismiss,
      title,
      onLoginSuccess,
      isBackButtonHidden,
    },
    modalScope: GLOBAL,
  });

export const openRegistrationDisabledModal = () =>
  openModal({ modalType: ModalType.REGISTRATION_DISABLED, modalScope: GLOBAL });

export const openUnsubscribeStreamer = ({ accountId, subscriptionId }) =>
  openModal({
    modalType: ModalType.UNSUBSCRIBE_STREAMER,
    modalData: {
      accountId,
      subscriptionId,
    },
  });

export const openProfileEditErrorModal = ({
  missingName,
  invalidBirthdate,
  minBirthdate,
  maxBirthdate,
}) =>
  openModal({
    modalType: ModalType.PROFILE_EDIT_ERROR,
    modalData: {
      missingName,
      invalidBirthdate,
      minBirthdate,
      maxBirthdate,
    },
  });

export const openAlertModal = ({
  imageUrl,
  title,
  body,
  buttonTitle,
  callback,
}) =>
  openModal({
    modalType: ModalType.ALERT,
    modalScope: GLOBAL,
    modalData: { imageUrl, title, body, buttonTitle, callback },
  });

export const openGiftsDrawerModal = ({
  tabId,
  canBackToGifts = true,
  target,
} = {}) =>
  openModal({
    modalType: ModalType.GIFTS_DRAWER,
    modalData: { tabId, canBackToGifts, target },
  });

export const openEditProfileModal = () =>
  openModal({ modalType: ModalType.EDIT_PROFILE, modalScope: GLOBAL });

export const openSendMediaToChatModal = (modalData) =>
  openModal({ modalType: ModalType.SEND_MEDIA_TO_CHAT, modalData });

export const openMiniProfileModal = ({
  accountId,
  streamId,
  followSource,
  isRelationToStreamer,
  modalScope = SCENE,
  initialState,
  miniProfileEntranceSource,
  additionalParams,
}) =>
  openModal({
    modalType: ModalType.MINI_PROFILE_MODAL,
    modalData: {
      accountId,
      streamId,
      followSource,
      isRelationToStreamer,
      initialState,
      miniProfileEntranceSource,
      additionalParams,
    },
    modalScope,
  });

export const openSubscribeModal = ({ accountId }) =>
  openModal({
    modalType: ModalType.SUBSCRIBE_MODAL,
    modalData: { accountId },
  });

export const openReportModal = ({ accountId }) =>
  openModal({
    modalType: ModalType.REPORT_PROFILE_MODAL,
    modalData: { accountId },
  });

export const openGiftFromBroadcasterModal = ({ receiverId, giftId, message }) =>
  openModal({
    modalType: ModalType.GIFT_FROM_BC_MODAL,
    modalData: { receiverId, giftId, message },
  });

export const openBlockListModal = () =>
  openModal({
    modalType: ModalType.BLOCK_LIST_MODAL,
    modalData: {},
  });

export const openConfirmationModal = ({
  title,
  confirmText,
  confirm,
  dismissText,
  dismiss,
  className,
  body,
  modalType = ModalType.CONFIRMATION_MODAL,
  modalScope = GLOBAL,
  swapActionButtons,
  ...rest
}) =>
  openModal({
    modalType,
    modalData: {
      title,
      confirmText,
      confirm,
      dismissText,
      dismiss,
      className,
      body,
      swapActionButtons,
      ...rest,
    },
    modalScope,
  });

export const openTcnnModal = (tcnnMessage) =>
  openModal({
    modalType: ModalType.TCNN_MODAL,
    modalData: tcnnMessage,
    modalScope: STREAM_SCOPE,
  });

export const openRedeemGiftCardModalView = ({ purchaseSource }) =>
  openModal({
    modalType: ModalType.REDEEM_GIFT_CARD_MODAL,
    modalScope: GLOBAL,
    modalData: { purchaseSource },
  });

export const openCaptchaModalView = () =>
  openModal({
    modalType: ModalType.CAPTCHA_MODAL,
    modalScope: GLOBAL,
    modalData: {
      disableEsc: true,
    },
  });

/**
 * @deprecated use openDeleteNftCardModal
 */
export const openRemoveNftCardModal = ({ confirm = false, accountId, lotId }) =>
  openModal({
    modalType: ModalType.NFT_REMOVE_CARD_MODAL,
    modalScope: GLOBAL,
    modalData: {
      confirm,
      accountId,
      lotId,
    },
  });

export const openDeleteNftCardModal = ({ accountId, lotId }) =>
  openModal({
    modalType: ModalType.NFT_DELETE_CARD_MODAL,
    modalScope: GLOBAL,
    modalData: {
      accountId,
      lotId,
    },
  });

export const openNotEnoughCoinsModal = ({
  purchaseSource,
  offer,
  streamerId,
  withBottomScreen = false,
  viewType,
  uiComponent,
  previousUiComponent,
  onSuccess,
  shouldDismissOnBack = false,
  offerConfig,
  isRefillV2DesktopEnabled,
}) => {
  const providers = getEnabledPaymentProviders();
  if (providers.length === 0) {
    return openCannotSendGiftModal();
  }

  if (withBottomScreen) {
    return (
      isRefillV2DesktopEnabled
        ? openRefillV2DrawerBottomScreen
        : openRefillDrawerBottomScreen
    )({
      screenData: {
        purchaseSource,
        viewType,
        offer,
        uiComponent,
        previousUiComponent,
        streamerId,
        onSuccess,
        shouldDismissOnBack,
        offerConfig,
      },
    });
  }

  return (isRefillV2DesktopEnabled ? openRefillModal : openBuyCoinsModal)({
    purchaseSource,
    offer,
    streamerId,
    uiComponent,
    previousUiComponent,
    viewType,
    onSuccess,
    shouldDismissOnBack,
    offerConfig,
  });
};

export const openLandingPageModalHandler = ({
  place,
  withBottomScreen = false,
  trackingId,
  onDismiss,
  isLandingPageV3Enabled = false,
}) => {
  if (withBottomScreen) {
    if (isLandingPageV3Enabled) {
      return openLandingV3BottomScreen({
        screenData: {
          place,
          trackingId,
          onDismiss,
        },
      });
    }

    return openLandingPageBottomScreen({
      screenData: {
        place,
        trackingId,
        onDismiss,
      },
    });
  }

  if (isLandingPageV3Enabled) {
    return openLandingV3Modal({
      place,
      trackingId,
      onDismiss,
    });
  }

  return openLandingPageModal({ place, trackingId, onDismiss });
};

export const openBottomSheetModalHandler = (
  modalData,
  withBottomScreen = false
) => {
  if (withBottomScreen) {
    return openBottomSheetBottomScreen({
      screenData: {
        ...modalData,
      },
    });
  }

  return openModal({
    modalType: ModalType.BOTTOM_SHEET,
    modalData,
    modalScope: STREAM_SCOPE,
  });
};

export const openPermissionRequestModal = (
  modalData,
  withBottomScreen = false
) => {
  if (withBottomScreen) {
    return openPushNotificationPermissionRequestBottomSheet({
      screenData: {
        ...modalData,
      },
    });
  }

  return openModal({
    modalType: ModalType.REQUEST_PUSH_NOTIFICATION_PERMISSION,
    modalData,
    modalScope: GLOBAL,
  });
};

export const openDeleteAccountModalHandler = ({ isMobile = false }) => {
  if (isMobile) {
    return openDeleteAccountBottomScreen();
  }

  return openDeleteAccountModal();
};

export const openEditPhoneNumberModalHandler = ({
  isMobile = false,
  isSecureYourAccount = false,
  disableOutsideClickDismiss = false,
  onDismiss = () => {},
  onOutsideClickDismiss,
}) => {
  if (isMobile) {
    return openEditPhoneNumberBottomScreen({
      onOutsideClickDismiss,
      isSecureYourAccount,
      disableOutsideClickDismiss,
      onDismiss,
    });
  }

  return openEditPhoneNumberModal({
    onOutsideClickDismiss,
    isSecureYourAccount,
  });
};

export const openDeletePhoneNumberModalHandler = ({ isMobile = false }) => {
  if (isMobile) {
    return openDeletePhoneNumberBottomScreen();
  }

  return openDeletePhoneNumberModal();
};

export const openEditEmailModalHandler = ({ isMobile = false, email }) => {
  if (isMobile) {
    return openEditEmailBottomScreen({ email });
  }

  return openEditEmailModal({ email });
};

export const openDeleteEmailModalHandler = ({ isMobile = false, email }) => {
  if (isMobile) {
    return openDeleteEmailBottomScreen({ email });
  }

  return openDeleteEmailModal({ email });
};

export const openEnterReferralCodeModalHandler = ({ isMobile = false }) => {
  if (isMobile) {
    return openEnterReferralCodeBottomScreen();
  }

  return openEnterReferralCodeModal();
};

export const openJoinAgencyInfoModalHandler = ({ isMobile = false }) => {
  if (isMobile) {
    return openJoinAgencyInfoBottomScreen();
  }

  return openJoinAgencyInfoModal();
};

export const openDeleteChatMessageModalHandler = ({
  isMobile = false,
  messages,
}) => {
  if (isMobile) {
    return openDeleteMessageBottomScreen({ messages });
  }

  return openDeleteChatMessageModal({ messages });
};

export const openDeleteBlockRequestModalHandler = ({ ...props }) => {
  if (props.isMobile) {
    return openConfirmationBottomScreen({ ...props });
  }

  return openConfirmationModal({ ...props });
};

export const openReferralHowItWorksModalHandler = () =>
  openModal({
    modalType: ModalType.REFERRAL_HOW_IT_WORKS_MODAL,
  });

export const openReferralAgencyTiersModalHandler = () =>
  openModal({
    modalType: ModalType.REFERRAL_AGENCY_TIERS_MODAL,
  });

export const openAcademyCongratulationModalHandler = (props) =>
  openModal({
    modalType: ModalType.ACADEMY_CONGRATULATION_MODAL,
    ...props,
  });

export const openAcademyCaptionsModalHandler = (props) =>
  openModal({
    modalType: ModalType.ACADEMY_CAPTIONS_MODAL,
    ...props,
  });

export const openSecureYourAccountModal = () =>
  openModal({
    modalType: ModalType.SECURE_YOUR_ACCOUNT,
    modalData: { disableEsc: true },
  });

export const openDeleteChatModalHandler = ({
  isMobile = false,
  conversationId,
}) => {
  if (isMobile) {
    return openDeleteChatBottomScreen(conversationId);
  }

  return openDeleteChatModal(conversationId);
};

export const openAppleAccountRestoration = ({ isMobile = false }) => {
  if (isMobile) {
    return openAppleAccountRestorationBottomScreen();
  }

  return openModal({
    modalType: ModalType.APPLE_ACCOUNT_RESOTRATION,
    modalData: {},
  });
};
